import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useHousehold } from '@contexts/HouseholdContext';

/**
 * Header dropdown for switching between households the user belongs to.
 *
 * Shows the current household name + a tiny subscription-status badge
 * (trialing / active / past_due) so members instantly know whether
 * streaming is gated by billing. Clicking the chevron opens a dropdown
 * with the other households + an "Add household" option.
 */
export default function HouseholdSwitcher() {
  const { households, activeId, activeHousehold, setActiveId, createHousehold } = useHousehold();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  if (households.length === 0) return null;

  const handleCreate = async () => {
    const name = prompt('New household name', 'My Household');
    if (!name?.trim()) return;
    setCreating(true);
    const res = await createHousehold(name.trim());
    setCreating(false);
    setOpen(false);
    if (res.ok) {
      toast.success(`Created "${name}"`);
      // Hard reload so every page picks up the new tenant scope cleanly.
      window.location.reload();
    } else {
      toast.error(res.error);
    }
  };

  const handlePick = (id: string) => {
    if (id !== activeId) {
      setActiveId(id);
      // Hard reload so cached lists in /providers, /members, dashboard
      // refetch under the new household context.
      window.location.reload();
    }
    setOpen(false);
  };

  const statusBadge = (() => {
    const s = activeHousehold?.subscription_status;
    if (!s || s === 'inactive') return null;
    const colors: Record<string, string> = {
      trialing: 'bg-blue-500/20 text-blue-300',
      active: 'bg-emerald-500/20 text-emerald-300',
      past_due: 'bg-amber-500/20 text-amber-300',
      canceled: 'bg-rose-500/20 text-rose-300',
    };
    return (
      <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${colors[s] ?? 'bg-zinc-700 text-zinc-300'}`}>
        {s}
      </span>
    );
  })();

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-zinc-100 hover:bg-zinc-700"
      >
        <span className="text-zinc-400">🏠</span>
        <span className="max-w-[14rem] truncate font-medium">{activeHousehold?.name ?? 'Pick household'}</span>
        {statusBadge}
        <span className="ml-1 text-zinc-500">▾</span>
      </button>

      {open && (
        <>
          {/* click-outside scrim */}
          <button
            type="button"
            aria-label="close"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default bg-transparent"
          />
          <div className="absolute right-0 z-20 mt-1 w-72 rounded-md border border-zinc-700 bg-zinc-900 py-1 shadow-xl">
            {households.map((h) => (
              <button
                key={h.id}
                type="button"
                onClick={() => handlePick(h.id)}
                className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-zinc-800 ${h.id === activeId ? 'bg-zinc-800/50' : ''}`}
              >
                <span className="truncate">
                  {h.name}
                  <span className="ml-2 text-[10px] uppercase tracking-wide text-zinc-500">{h.role}</span>
                </span>
                {h.id === activeId && <span className="text-emerald-400">✓</span>}
              </button>
            ))}
            <div className="my-1 border-t border-zinc-800" />
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/household'); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800"
            >
              <span className="text-zinc-400">⚙</span>
              Manage household
            </button>
            <button
              type="button"
              disabled={creating}
              onClick={handleCreate}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800 disabled:opacity-50"
            >
              <span className="text-zinc-400">+</span>
              {creating ? 'Creating…' : 'New household'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
